'use client'

import React, {FunctionComponent, ReactNode, useCallback, useEffect, useRef} from 'react'
import Tabs from '@/components/Tabs/Tabs'

import useRouter from "@/utils/Router";
import { useSearchParams as useRRSearchParams } from 'react-router-dom'

import {MenuItem} from '@/types/uiElements'
import Popover from "@/components/UiElements/PopOver";

interface HeadingProps {
    title: string | ReactNode
    subTitle?: string | ReactNode | null
    mode?: string | null
    description?: string | ReactNode | null
    parent?: {
        title: string | null
        url: string
    } | null
    help?: {
        title: string
        url?: string
        onClick?: () => void
    } | null
    children?: ReactNode
    childrenClassName?: string
    contentClassName?: string
    tab?: {
        tabs: Tab[]
        selectedTab: number
        onSelectedChange: (val: number) => void
	    changeSort?: null | ((nextOptions: MenuItem[]) => void)
    } | null,
    options?: MenuItem[],

}


interface Tab {
    name: string
    content?: React.ReactNode
	action?: ()=>void
	actionIcon?: string
}

const Heading: FunctionComponent<HeadingProps> = (
    {
        title,
        subTitle,
        description,
        parent,
        help,
        children,
        tab,
        mode,
        childrenClassName,
        contentClassName,
        options,
    }
) => {
    const [scrolled, setScrolled] = React.useState(false);
    const containerRef = React.useRef<HTMLDivElement>(null);



    const handleScroll = () => {
        const scrollHeight = (containerRef.current?.offsetHeight || 0) + 75;
        if (window.scrollY > scrollHeight) {
            setScrolled(true);
        } else {
            setScrolled(false);
        }
    }

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        }
    }, [])


    const Router = useRouter()

    // Sync tab selection with URL ?tab= param
    const [searchParams, setSearchParams] = useRRSearchParams()
    const tabNamesRef = useRef<string[]>([])

    const getTabNames = () => (tab?.tabs || []).map((t) => (typeof t.name === 'string' ? t.name : '')).filter(Boolean)

    useEffect(() => {
        if (!tab) return
        const names = getTabNames()
        tabNamesRef.current = names
        const tabParam = searchParams.get('tab')
        if (!tabParam) return
        const index = names.findIndex((n) => n.toLowerCase() === tabParam.toLowerCase())
        if (index >= 0 && index !== tab.selectedTab) {
            tab.onSelectedChange(index)
        }
    }, [tab?.tabs.length, tab?.tabs.map((t) => (typeof t.name === 'string' ? t.name : '')).join(',')])

    const handleTabChange = useCallback((index: number) => {
        if (!tab) return
        tab.onSelectedChange(index)
        const names = getTabNames()
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev)
                if (index === 0) {
                    next.delete('tab')
                } else {
                    next.set('tab', names[index] || '')
                }
                return next
            },
            { replace: true },
        )
    }, [tab, setSearchParams])

    return (
        <>

            <div
                ref={containerRef}
                className={`w-[100%] z-[15] ${
                    parent ? 'px-xxl' : 'px-md'
                } pt-xl bg-gray-50 border-b border-gray-200 justify-start items-start gap-lg inline-flex ${mode === 'brief' ? '' : 'mb-xxl'}`}
            >
                <div className="w-full justify-start items-start gap-lg inline-flex">
                    {parent && (
                        <div
                            className="w-[37px] h-[37px] min-w-[37px] bg-gray-900 rounded-full flex-col justify-center items-center inline-flex font-icon-regular hover:bg-gray-700 cursor-pointer transition-all"
                            onClick={()=>Router.push(parent.url)}

                        >
                            <div className="text-center text-white "></div>
                        </div>
                    )}
                    <div className={`flex flex-col gap-0 ${contentClassName || ''}`}>
                        <div className={'gap-xs pb-xl flex flex-col'}>
                            {/*Parent*/}
                            {parent && parent.title && (
                                <div className="h-[37px] items-center inline-flex text-gray-900 text-title-md "
                                >
                                    {parent.title}
                                </div>
                            )}

                            {/*Main Header*/}
                            <div className="text-gray-900 text-title-xxl capitalize ">{title}{subTitle && subTitle}</div>

                            {/*Description*/}
                            <div className="text-text-md text-gray-400 gap-sm inline-flex items-center">
                                {description}
                                {help && (
                                    <>
                                        {description && <span className="font-icon-light text-gray-400"></span>}
                                        {help.onClick ? (
                                            <button
                                                type="button"
                                                onClick={help.onClick}
                                                aria-haspopup="dialog"
                                                className="inline-flex cursor-pointer text-title-md text-gray-500 underline underline-offset-2 transition-colors hover:text-gray-900"
                                            >
                                                {help.title}
                                            </button>
                                        ) : (
                                            <a href={help.url || '/help'} className="inline-flex items-center gap-xs text-title-md text-primary-600">
                                                {help.title}
                                                <span className="font-icon-regular"></span>
                                            </a>
                                        )}
                                    </>
                                )}
                            </div>


                        </div>
                        {children && <div className={`pb-xl ${childrenClassName || ""}`}>{children}</div>}
                        {tab && (
                            <div>
                                <Tabs
	                                tabs={tab.tabs}
	                                selectedTab={tab.selectedTab}
	                                onSelectedChange={handleTabChange}
	                                changeSort={tab.changeSort || null}
                                />
                            </div>
                        )}
                    </div>

                    {
                        (options && options.length > 0) && (
                            <div className={"flex flex-grow justify-end select-none"}>
                                <Popover
                                    toggle={(open) => (
                                        <div
                                            className={` select-none w-[37px] h-[37px] min-w-[37px] ${open ? "text-white bg-gray-900" : " bg-gray-200 hover:bg-gray-700 text-gray-900 hover:text-white"}     rounded-full flex-col justify-center items-center inline-flex font-icon-regular cursor-pointer transition-all`}


                                        >
                                            <div className="text-center ">ellipsis</div>
                                        </div>
                                    )}
                                    placement="bottom right"
                                    offsetX={0}
                                    offsetY={10}
                                    menu={options}
                                />


                            </div>

                        )
                    }
                </div>
            </div>
            {/*<div className={` bg-white w-full px-md ${parent ? "pl-[107px] z-[14] shadow-md" : "z-[1]"} fixed top-md transition-opacity text-title-lg gap-sm items-center duration-500 flex pt-[14px] pb-md  ${scrolled ? "!top-[0px] opacity-100" : "top-[-200px] opacity-0"}`}>*/}
            <div className={`${parent ? "pl-[107px] z-[14] shadow-md" : "z-[14]"} fixed top-md flex h-[48px] w-full items-center gap-sm bg-white px-md text-title-lg transition-opacity duration-500 ${scrolled ? "!top-[0px] opacity-100" : "hidden !top-[-200px] !z-[0] opacity-0"}`}>
                {title}{subTitle && subTitle}
                {/*{description &&
                    <span className="font-icon-light text-gray-400"></span>
                }*/}
                {/*{description &&
                    <div className="text-text-md text-gray-400 gap-sm inline-flex items-center">
                        {description}
                    </div>
                }*/}
            </div>
        </>

    )
}

export default Heading
